// ============================================================================
// Gmail — `users.messages.send` helpers.
//
// Every call targets `https://gmail.googleapis.com/gmail/v1/users/me/...`
// with the user's OAuth access token in the `Authorization: Bearer` header
// (NEVER in the URL — edge logs capture query strings).
//
// Gmail accepts either standard base64 or base64url for the `raw` field; we
// always send base64url because url-safe blobs flow through JSON, querystring,
// and webhook debug payloads without extra transforms.
//
// Size limit:
//   Gmail rejects messages > 35MB raw. We check BEFORE hitting the network so
//   the caller sees `ATTACHMENT_TOO_LARGE` without paying a round trip.
// ============================================================================

import type { EmailSendInput, EmailAddress } from '../../capabilities/email'
import { GMAIL_API_BASE } from './oauth'
import { base64UrlEncodeMessage, buildMimeMessage, parseMimeMessage } from './mime'
import {
  classifyGmailError,
  GmailError,
  parseRetryAfterMs,
  sanitizeGmailErrorMessage,
  type GmailSendResponse,
  type GoogleErrorEnvelope,
} from './types'

// ============================================================================
// Constants
// ============================================================================

/** Gmail raw message size limit (35MB per Google's documented cap). */
export const GMAIL_MAX_RAW_SIZE = 35 * 1024 * 1024

// ============================================================================
// Public types
// ============================================================================

export interface SendGmailMessageInput {
  accessToken: string
  input: EmailSendInput
  sender?: EmailAddress
  fetchImpl?: typeof fetch
  /** Override the API base (tests). */
  apiBase?: string
}

export interface SendGmailMessageOutput {
  messageId: string
  threadId: string
}

// ============================================================================
// sendGmailMessage — build MIME + POST to messages.send
// ============================================================================

/**
 * Send an email via Gmail. When `input.replyToMessageId` is set we:
 *   1. GET the referenced message to learn its `threadId` +
 *      `Message-Id` header (stamped as `In-Reply-To`).
 *   2. Build MIME with the `In-Reply-To` + `References` headers.
 *   3. POST `{ raw, threadId }` so Gmail keeps the reply threaded.
 *
 * Throws `GmailError` on any non-2xx response. The caller (dispatcher /
 * chat hook) is expected to react on `.code`.
 */
export async function sendGmailMessage(
  args: SendGmailMessageInput,
): Promise<SendGmailMessageOutput> {
  assertAccessToken(args.accessToken)
  if (!args.input) {
    throw new GmailError('INVALID_INPUT', 'sendGmailMessage: input is required')
  }
  const fetchImpl = args.fetchImpl ?? fetch
  const apiBase = args.apiBase ?? GMAIL_API_BASE

  // ── Reply threading: resolve the target thread + stamp headers.
  let threadId: string | undefined
  let inReplyTo: string | undefined
  let references: string[] | undefined
  if (args.input.replyToMessageId) {
    const parent = await fetchReplyParent(
      args.input.replyToMessageId,
      args.accessToken,
      fetchImpl,
      apiBase,
    )
    threadId = parent.threadId
    inReplyTo = parent.messageIdHeader
    references = parent.references
  }

  // Build raw MIME. We inject In-Reply-To / References ourselves (the builder
  // doesn't look up `replyToMessageId`; that's the send path's job).
  let raw = buildMimeMessage(args.input, args.sender)
  if (inReplyTo) {
    // Issue #254 L2: dedup the References chain before joining. The parent's
    // `References` already terminates in the parent's Message-ID in many
    // clients (Thunderbird, Apple Mail, ProtonMail), so appending
    // `inReplyTo` unconditionally would emit the same id twice. The order-
    // preserving dedup below is also safe for the clients that DON'T
    // auto-close the chain — they just see the expected `references + parent`
    // append.
    const chain: string[] = []
    const seen = new Set<string>()
    for (const ref of references ?? []) {
      if (!seen.has(ref)) {
        seen.add(ref)
        chain.push(ref)
      }
    }
    if (!seen.has(inReplyTo)) {
      chain.push(inReplyTo)
    }
    raw = injectHeaders(raw, {
      'In-Reply-To': inReplyTo,
      References: chain.join(' '),
    })
  }

  // Size guard — Round 1 C1 fix: Gmail's 35 MB cap applies to the
  // base64url-encoded `raw` field that actually goes on the wire, NOT the
  // pre-encoded MIME string. Base64url inflates by ~4/3, so a 34.9 MB MIME
  // encodes to ~46 MB and would be rejected by Gmail AFTER the POST.
  //
  // Two-step check:
  //   1. If the raw MIME alone already exceeds the cap, bail out early
  //      without spending ~n CPU on the encode (the encoded form can only
  //      be bigger).
  //   2. Otherwise compute the encoded length and compare — this catches
  //      the 26–34 MB MIME range that inflates across the limit.
  const rawBytes = new TextEncoder().encode(raw).byteLength
  if (rawBytes > GMAIL_MAX_RAW_SIZE) {
    throw new GmailError(
      'ATTACHMENT_TOO_LARGE',
      `raw message is ${rawBytes} bytes; exceeds Gmail's 35MB cap`,
      { httpStatus: 0 },
    )
  }
  const encoded = base64UrlEncodeMessage(raw)
  if (encoded.length > GMAIL_MAX_RAW_SIZE) {
    throw new GmailError(
      'ATTACHMENT_TOO_LARGE',
      `encoded message is ${encoded.length} bytes; exceeds Gmail's 35MB raw cap`,
      { httpStatus: 0 },
    )
  }

  const body: Record<string, string> = { raw: encoded }
  if (threadId) body.threadId = threadId

  const url = `${apiBase}/gmail/v1/users/me/messages/send`
  const response = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${args.accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  const parsed = (await response.json()) as GmailSendResponse
  return {
    messageId: parsed.id,
    threadId: parsed.threadId,
  }
}

// ============================================================================
// Reply-parent lookup
// ============================================================================

interface ReplyParent {
  threadId: string
  /** The `Message-ID` header of the parent (used for In-Reply-To). */
  messageIdHeader?: string
  /** References chain from the parent (used to extend our References). */
  references?: string[]
}

async function fetchReplyParent(
  messageId: string,
  accessToken: string,
  fetchImpl: typeof fetch,
  apiBase: string,
): Promise<ReplyParent> {
  // Gmail `messages.get?format=full` returns `payload.headers[]` — cheaper
  // than `format=raw`, and we only need Message-Id + Threading headers.
  const url = `${apiBase}/gmail/v1/users/me/messages/${encodeURIComponent(messageId)}?format=metadata&metadataHeaders=Message-Id&metadataHeaders=References&metadataHeaders=Subject`
  const resp = await fetchImpl(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!resp.ok) {
    throw await errorFromResponse(resp)
  }
  const body = (await resp.json()) as {
    threadId?: string
    payload?: { headers?: Array<{ name?: string; value?: string }> }
  }
  // Issue #254 M3: a 200 response without `threadId` is malformed per Google's
  // Gmail API docs (the Message resource ALWAYS carries threadId). Distinguish
  // this from "message not found" (which surfaces as 404 upstream) so callers
  // handling MESSAGE_NOT_FOUND aren't misled into cleaning up a message that
  // actually exists — the real issue is on Google's side.
  if (!body || typeof body.threadId !== 'string') {
    throw new GmailError(
      'PROVIDER_ERROR',
      `reply parent ${messageId} returned 200 without threadId — malformed Gmail response`,
      {},
    )
  }
  const headers = Array.isArray(body.payload?.headers) ? body.payload!.headers! : []
  const msgIdHeader = findHeader(headers, 'Message-Id')
  const refsHeader = findHeader(headers, 'References')
  const references =
    refsHeader && refsHeader.trim().length > 0
      ? refsHeader.split(/\s+/).filter((s) => s.length > 0)
      : undefined
  return {
    threadId: body.threadId,
    messageIdHeader: msgIdHeader,
    references,
  }
}

function findHeader(
  headers: Array<{ name?: string; value?: string }>,
  name: string,
): string | undefined {
  const target = name.toLowerCase()
  for (const h of headers) {
    if (typeof h?.name === 'string' && h.name.toLowerCase() === target && typeof h.value === 'string') {
      return h.value
    }
  }
  return undefined
}

// ============================================================================
// Header injection — inject / overwrite specific header lines on a built MIME
// string without re-building the whole message.
// ============================================================================

function injectHeaders(raw: string, overrides: Record<string, string>): string {
  // Build a case-insensitive lookup so callers can pass either "In-Reply-To"
  // or "in-reply-to" and we'll still match / replace the existing header.
  const lowerKeyMap: Record<string, { displayName: string; value: string }> = {}
  for (const [k, v] of Object.entries(overrides)) {
    lowerKeyMap[k.toLowerCase()] = { displayName: k, value: v }
  }

  const splitIdx = raw.indexOf('\r\n\r\n')
  if (splitIdx < 0) {
    // Fallback: no headers/body boundary; prepend. Should not happen for
    // our builder output but guards against upstream format changes.
    const prelude = Object.entries(overrides)
      .map(([k, v]) => `${k}: ${v}`)
      .join('\r\n')
    return `${prelude}\r\n${raw}`
  }
  const headerText = raw.slice(0, splitIdx)
  const body = raw.slice(splitIdx)
  const lines = headerText.split('\r\n')
  const seen = new Set<string>()
  const out: string[] = []
  for (const line of lines) {
    const idx = line.indexOf(':')
    if (idx <= 0) {
      out.push(line)
      continue
    }
    const name = line.slice(0, idx).trim().toLowerCase()
    const override = lowerKeyMap[name]
    if (override) {
      out.push(`${override.displayName}: ${override.value}`)
      seen.add(name)
      continue
    }
    out.push(line)
  }
  for (const [lower, entry] of Object.entries(lowerKeyMap)) {
    if (!seen.has(lower)) out.push(`${entry.displayName}: ${entry.value}`)
  }
  return `${out.join('\r\n')}${body}`
}

// ============================================================================
// Error helpers
// ============================================================================

async function errorFromResponse(response: Response): Promise<GmailError> {
  let envelope: GoogleErrorEnvelope | null = null
  let text = ''
  try {
    text = await response.text()
    envelope = text ? (JSON.parse(text) as GoogleErrorEnvelope) : null
  } catch {
    envelope = null
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  // If the body wasn't JSON, carry the first ~200 sanitised chars into the
  // error message so operators still see SOMETHING useful in tail logs.
  if (!envelope && text) {
    return new GmailError(
      response.status === 401 ? 'AUTH_FAILED' : 'PROVIDER_ERROR',
      sanitizeGmailErrorMessage(text).slice(0, 200),
      { httpStatus: response.status, retryAfterMs },
    )
  }
  return classifyGmailError(
    response.status,
    envelope?.error?.errors?.[0]?.reason,
    envelope,
    retryAfterMs,
  )
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GmailError('INVALID_INPUT', 'accessToken is required')
  }
}

// ============================================================================
// Re-exports used by capability-methods.ts
// ============================================================================

export { parseMimeMessage }
